import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  X,
  Mail,
  KeyRound,
  User,
  Phone,
  Store,
  ArrowRight,
  Sparkles,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';
import { NofeeNavLogo } from './NoFeeLogo';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * RestaurantAuthModal — premium dark-mode access flow for restaurant owners.
 *
 * Replaces the trio "For Restaurants / Restaurant Login / Get Started Free"
 * with a single welcoming entry point. Opens from the global Navbar's
 * "Restaurants" button. Tab pattern with SIGN IN as the default tab so
 * existing owners never see a signup screen by mistake. Mobile = full screen.
 */

const SignInForm = ({ onSwitchToSignup, onClose }) => {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [view, setView] = useState('password'); // 'password' | 'magic-request' | 'magic-sent'
  const [form, setForm] = useState({ email: '', password: '' });
  const [submitting, setSubmitting] = useState(false);

  const onChange = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.email.trim() || !form.password.trim()) {
      toast.error('Enter your email and password.');
      return;
    }
    setSubmitting(true);
    try {
      const result = await login(form.email.trim().toLowerCase(), form.password);
      onClose?.();
      // iter124 — Sign In always routes to the Dashboard, regardless of
      // subscription / onboarding state. The Dashboard already shows a
      // "Continue setup" card and banners for owners mid-wizard. Sending
      // existing customers back into the wizard on every sign in was
      // hiding their Overview, Settings, Stripe and other management
      // sections — which is what they're signing in to reach.
      void result;
      navigate('/dashboard');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Invalid email or password.');
    } finally {
      setSubmitting(false);
    }
  };

  // Magic-link (passwordless) — calls the restaurant magic-link endpoint.
  // The endpoint may not be implemented for restaurant owners yet; if it
  // returns 404 we fall back to a friendly hint pointing to support.
  const submitMagicLink = async (e) => {
    e.preventDefault();
    const email = form.email.trim().toLowerCase();
    if (!email || !email.includes('@')) {
      toast.error('Enter a valid email to receive your sign-in link.');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/api/auth/magic-link`, { email });
      setView('magic-sent');
    } catch (err) {
      const code = err?.response?.status;
      if (code === 404) {
        toast.info(
          "Magic-link is rolling out for restaurants. For now, please sign in with your password or contact support@nofee.restaurant.",
        );
        setView('password');
      } else {
        toast.error(err?.response?.data?.detail || 'Could not send your sign-in link.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const inputBase =
    'w-full pl-11 pr-4 py-3.5 rounded-xl bg-white/[0.04] border border-white/10 ' +
    'text-white placeholder-white/45 text-[15px] ' +
    'focus:outline-none focus:border-yellow-500/60 focus:bg-white/[0.06] transition-colors';

  if (view === 'magic-sent') {
    return (
      <div className="text-center py-3" data-testid="rest-magic-link-sent">
        <div className="mx-auto w-14 h-14 rounded-2xl bg-emerald-500/[0.10] border border-emerald-500/30 flex items-center justify-center mb-4">
          <Mail className="w-6 h-6 text-emerald-300" strokeWidth={1.75} />
        </div>
        <h3 className="text-white text-[17px] font-bold tracking-tight">Check your inbox</h3>
        <p className="mt-2 text-[13px] text-white/60 leading-relaxed">
          If a restaurant account exists for <strong className="text-white">{form.email.trim().toLowerCase()}</strong>, a one-click sign-in link is on its way. The link expires in 15 minutes.
        </p>
        <button
          type="button"
          onClick={() => setView('password')}
          data-testid="rest-magic-back-to-password"
          className="mt-5 text-[13px] text-yellow-300 hover:text-yellow-200 font-bold"
        >
          Back to password sign in
        </button>
      </div>
    );
  }

  if (view === 'magic-request') {
    return (
      <form onSubmit={submitMagicLink} data-testid="rest-magic-link-form" className="space-y-3">
        <div className="relative">
          <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4.5 h-4.5 text-yellow-400/80" />
          <input
            type="email"
            placeholder="Email address"
            value={form.email}
            onChange={onChange('email')}
            data-testid="rest-magic-link-input"
            className={inputBase}
            required
            autoFocus
          />
        </div>
        <button
          type="submit"
          disabled={submitting}
          data-testid="rest-magic-link-submit"
          className="w-full py-3.5 rounded-xl bg-gradient-to-b from-yellow-300 to-yellow-500 text-black font-extrabold text-[15px] tracking-tight hover:from-yellow-200 hover:to-yellow-400 hover:-translate-y-0.5 disabled:opacity-60 disabled:cursor-not-allowed transition-all shadow-[0_15px_40px_-15px_rgba(250,204,21,0.55)]"
        >
          {submitting ? 'Sending…' : 'Email me a sign-in link'}
        </button>
        <p className="text-center text-[12.5px] text-white/55">
          Prefer your password?{' '}
          <button
            type="button"
            onClick={() => setView('password')}
            className="text-yellow-300 hover:text-yellow-200 font-bold"
          >
            Sign in with password
          </button>
        </p>
      </form>
    );
  }

  return (
    <form onSubmit={submit} data-testid="rest-login-form" className="space-y-3">
      <div className="relative">
        <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4.5 h-4.5 text-yellow-400/80" />
        <input
          type="email"
          placeholder="Email address"
          value={form.email}
          onChange={onChange('email')}
          data-testid="rest-login-input-email"
          className={inputBase}
          required
          autoFocus
        />
      </div>
      <div className="relative">
        <KeyRound className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4.5 h-4.5 text-yellow-400/80" />
        <input
          type="password"
          placeholder="Password"
          value={form.password}
          onChange={onChange('password')}
          data-testid="rest-login-input-password"
          className={inputBase}
          required
        />
      </div>

      <div className="flex items-center justify-end">
        <button
          type="button"
          onClick={() => setView('magic-request')}
          data-testid="rest-login-forgot"
          className="text-[12px] text-yellow-300/85 hover:text-yellow-200 font-medium transition-colors"
        >
          Forgot password?
        </button>
      </div>

      <button
        type="submit"
        disabled={submitting}
        data-testid="rest-login-submit"
        className="mt-2 w-full py-3.5 rounded-xl bg-gradient-to-b from-yellow-300 to-yellow-500 text-black font-extrabold text-[15px] tracking-tight hover:from-yellow-200 hover:to-yellow-400 hover:-translate-y-0.5 disabled:opacity-60 disabled:cursor-not-allowed transition-all shadow-[0_15px_40px_-15px_rgba(250,204,21,0.55)]"
      >
        {submitting ? 'Signing in…' : 'Sign in'}
      </button>

      <p className="text-center text-[12.5px] text-white/55 pt-2">
        New here?{' '}
        <button
          type="button"
          onClick={onSwitchToSignup}
          data-testid="rest-switch-to-signup"
          className="text-yellow-300 hover:text-yellow-200 font-bold"
        >
          Create your restaurant account
        </button>
      </p>
    </form>
  );
};

const CreateAccountForm = ({ onSwitchToSignIn }) => {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    restaurant_name: '',
    name: '',
    email: '',
    phone: '',
    password: '',
  });
  const [submitting, setSubmitting] = useState(false);

  const onChange = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (
      !form.restaurant_name.trim() ||
      !form.name.trim() ||
      !form.email.trim() ||
      !form.phone.trim() ||
      !form.password.trim()
    ) {
      toast.error('Please fill in all fields.');
      return;
    }
    if (form.password.length < 6) {
      toast.error('Password must be at least 6 characters.');
      return;
    }
    setSubmitting(true);
    try {
      // Use AuthContext.register so the in-memory session is hydrated before
      // navigating — otherwise the protected onboarding route bounces the new
      // owner back to /login.
      await register({
        name: form.name.trim(),
        email: form.email.trim().toLowerCase(),
        phone: form.phone.trim(),
        password: form.password,
        restaurant_name: form.restaurant_name.trim(),
      });
      toast.success('Welcome to NoFee!');
      // New guided onboarding: account → payouts trust → Stripe → plan → subscribe → setup.
      setTimeout(() => navigate('/onboarding/payouts'), 400);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not create your account.');
    } finally {
      setSubmitting(false);
    }
  };

  const inputBase =
    'w-full pl-11 pr-4 py-3.5 rounded-xl bg-white/[0.04] border border-white/10 ' +
    'text-white placeholder-white/45 text-[15px] ' +
    'focus:outline-none focus:border-yellow-500/60 focus:bg-white/[0.06] transition-colors';

  return (
    <form onSubmit={submit} data-testid="rest-signup-form" className="space-y-3">
      <div className="relative">
        <Store className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4.5 h-4.5 text-yellow-400/80" />
        <input
          type="text"
          placeholder="Restaurant name"
          value={form.restaurant_name}
          onChange={onChange('restaurant_name')}
          data-testid="rest-signup-restaurant"
          className={inputBase}
          required
          autoFocus
        />
      </div>
      <div className="relative">
        <User className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4.5 h-4.5 text-yellow-400/80" />
        <input
          type="text"
          placeholder="Your name"
          value={form.name}
          onChange={onChange('name')}
          data-testid="rest-signup-name"
          className={inputBase}
          required
        />
      </div>
      <div className="relative">
        <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4.5 h-4.5 text-yellow-400/80" />
        <input
          type="email"
          placeholder="Email address"
          value={form.email}
          onChange={onChange('email')}
          data-testid="rest-signup-email"
          className={inputBase}
          required
        />
      </div>
      <div className="relative">
        <Phone className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4.5 h-4.5 text-yellow-400/80" />
        <input
          type="tel"
          placeholder="Phone (e.g. +1 555 555 5555)"
          value={form.phone}
          onChange={onChange('phone')}
          data-testid="rest-signup-phone"
          className={inputBase}
          required
        />
      </div>
      <div className="relative">
        <KeyRound className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4.5 h-4.5 text-yellow-400/80" />
        <input
          type="password"
          placeholder="Create a password (min. 6 chars)"
          value={form.password}
          onChange={onChange('password')}
          data-testid="rest-signup-password"
          className={inputBase}
          required
          minLength={6}
        />
      </div>

      <button
        type="submit"
        disabled={submitting}
        data-testid="rest-signup-submit"
        className="mt-2 w-full inline-flex items-center justify-center gap-2 py-3.5 rounded-xl bg-gradient-to-b from-yellow-300 to-yellow-500 text-black font-extrabold text-[15px] tracking-tight hover:from-yellow-200 hover:to-yellow-400 hover:-translate-y-0.5 disabled:opacity-60 disabled:cursor-not-allowed transition-all shadow-[0_15px_40px_-15px_rgba(250,204,21,0.55)]"
      >
        <Sparkles className="w-4 h-4" />
        {submitting ? 'Creating your account…' : 'Create restaurant account'}
        <ArrowRight className="w-4 h-4" />
      </button>

      <p className="text-center text-[11.5px] text-white/40 px-2">
        Free to start. No credit card required to create your account.
      </p>
      <p className="text-center text-[12.5px] text-white/55">
        Already a member?{' '}
        <button
          type="button"
          onClick={onSwitchToSignIn}
          data-testid="rest-switch-to-signin"
          className="text-yellow-300 hover:text-yellow-200 font-bold"
        >
          Sign in
        </button>
      </p>
    </form>
  );
};

const RestaurantAuthModal = ({ open, initialMode = 'login', onClose }) => {
  const { t } = useTranslation();
  const [mode, setMode] = useState(initialMode);

  useEffect(() => {
    if (open) setMode(initialMode);
  }, [open, initialMode]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      data-testid="restaurant-auth-modal"
      className="fixed inset-0 z-[100] flex items-center justify-center p-0 sm:p-6 bg-black/85 backdrop-blur-sm overflow-y-auto"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative w-full sm:max-w-5xl max-h-[100vh] sm:max-h-[92vh] overflow-y-auto sm:rounded-3xl bg-gradient-to-br from-zinc-950 via-black to-zinc-950 border-0 sm:border sm:border-yellow-500/15 shadow-[0_50px_120px_-30px_rgba(0,0,0,0.95)] grid grid-cols-1 md:grid-cols-2"
      >
        {/* LEFT — simplified marketing banner.
            Three-line hierarchy ("More Orders. Keep 100% of Every Order.
            No Commission.") replaces the previous overlapping copy so
            brand recognition + readability come first. */}
        <div className="hidden md:flex relative bg-black overflow-hidden">
          <img
            src="/brand/nofee_homepage.jpg"
            alt="Restaurant owner using NoFee"
            loading="lazy"
            className="absolute inset-0 w-full h-full object-cover opacity-55"
          />
          {/* Single, top-down vignette for legibility — no overlapping layers. */}
          <div className="absolute inset-0 bg-gradient-to-b from-black/55 via-black/55 to-black/85" />
          <div className="relative z-10 flex flex-col justify-between p-9 lg:p-12 text-white w-full">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-yellow-500/30 bg-yellow-500/[0.08] backdrop-blur-sm self-start">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
              <span className="text-[10.5px] uppercase tracking-[0.22em] text-yellow-300 font-bold">
                {t('auth_modal.eyebrow', 'Restaurants on NoFee')}
              </span>
            </div>

            <div className="mt-auto">
              {/* The three-line hierarchy. Each on its own line, with the
                  middle line in gold so the eye lands there first. */}
              <h2
                data-testid="rest-auth-banner-headline"
                className="font-['Outfit',_Inter,_sans-serif] text-[40px] lg:text-[52px] font-semibold tracking-[-0.035em] leading-[1.02] text-white"
              >
                {t('auth_modal.banner_line_1', 'More Orders.')}
                <br />
                <span className="bg-gradient-to-r from-yellow-300 to-amber-500 bg-clip-text text-transparent">
                  {t('auth_modal.banner_line_2', 'Keep 100% of Every Order.')}
                </span>
                <br />
                {t('auth_modal.banner_line_3', 'No Commission.')}
              </h2>
            </div>
          </div>
        </div>

        {/* RIGHT — auth card */}
        <div className="relative p-6 sm:p-9 lg:p-11">
          {/* Clickable brand lockup — closes the modal then navigates to /.
              Sits at the top-left so it anchors the brand even when the
              banner is hidden on mobile. */}
          <Link
            to="/"
            onClick={onClose}
            data-testid="rest-auth-brand-link"
            aria-label="NoFee — back to homepage"
            className="inline-flex flex-col items-start gap-1 group"
          >
            <NofeeNavLogo height={32} variant="light" />
            <span className="text-[10.5px] uppercase tracking-[0.22em] text-yellow-300/85 font-bold group-hover:text-yellow-200 transition-colors">
              {t('auth_modal.portal_subtitle', 'Restaurant Portal')}
            </span>
          </Link>

          <button
            type="button"
            onClick={onClose}
            data-testid="rest-auth-modal-close"
            aria-label="Close"
            className="absolute top-4 right-4 w-10 h-10 rounded-full border border-white/10 hover:border-yellow-500/40 hover:bg-white/[0.04] flex items-center justify-center text-white/60 hover:text-yellow-300 transition-colors"
          >
            <X className="w-4.5 h-4.5" />
          </button>

          <h3
            className="font-['Outfit',_Inter,_sans-serif] text-[26px] sm:text-[28px] font-semibold tracking-[-0.025em] text-white mt-7"
            data-testid="rest-auth-headline"
          >
            {mode === 'login' ? 'Sign in to your restaurant' : 'Create your restaurant account'}
          </h3>
          <p className="mt-2 text-[13.5px] text-white/55 max-w-sm leading-relaxed">
            {mode === 'login'
              ? 'Access your dashboard, menu, orders and payouts in one place.'
              : 'Start with 0% commission. Free to create — no credit card required.'}
          </p>

          {/* Tab switcher — Sign in active by default */}
          <div
            className="grid grid-cols-2 p-1 mt-6 rounded-2xl bg-white/[0.04] border border-white/8"
            role="tablist"
            data-testid="rest-auth-tabs"
          >
            <button
              type="button"
              role="tab"
              aria-selected={mode === 'login'}
              onClick={() => setMode('login')}
              data-testid="rest-auth-tab-login"
              className={`py-2.5 rounded-xl text-[13px] font-bold transition-all ${
                mode === 'login'
                  ? 'bg-gradient-to-b from-yellow-300 to-yellow-500 text-black shadow-[0_8px_20px_-8px_rgba(250,204,21,0.55)]'
                  : 'text-white/65 hover:text-white'
              }`}
            >
              Sign in
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={mode === 'signup'}
              onClick={() => setMode('signup')}
              data-testid="rest-auth-tab-signup"
              className={`py-2.5 rounded-xl text-[13px] font-bold transition-all ${
                mode === 'signup'
                  ? 'bg-gradient-to-b from-yellow-300 to-yellow-500 text-black shadow-[0_8px_20px_-8px_rgba(250,204,21,0.55)]'
                  : 'text-white/65 hover:text-white'
              }`}
            >
              Create account
            </button>
          </div>

          <div className="mt-6">
            {mode === 'login' ? (
              <SignInForm
                onSwitchToSignup={() => setMode('signup')}
                onClose={onClose}
              />
            ) : (
              <CreateAccountForm onSwitchToSignIn={() => setMode('login')} />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default RestaurantAuthModal;
